import { Package } from "lucide-react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "#app/components/ui/badge";
import {
  resolveMarketplaceOnboardInstallType,
  type MarketplaceOnboardInstallType,
} from "#app/utils/marketplace/employee-pack";
import { cn } from "#app/utils/misc";

type InstallTypeMeta = {
  icon: ReactNode;
  badgeClassName: string;
  surfaceClassName: string;
};

const MARKETPLACE_INSTALL_TYPE_META: Record<
  MarketplaceOnboardInstallType,
  InstallTypeMeta
> = {
  single: {
    icon: <img src="/img/solo-agent.svg" className="w-5 h-5" alt="" />,
    badgeClassName:
      "border border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-900/30 dark:text-blue-300",
    surfaceClassName:
      "border-blue-100 bg-[linear-gradient(180deg,#F8FBFF_0%,#FFFFFF_100%)] dark:border-blue-900 dark:bg-none dark:bg-blue-950/40",
  },
  normal_pack: {
    icon: <img src="/img/team-pack.svg" className="w-5 h-5" alt="" />,
    badgeClassName:
      "border border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900 dark:bg-orange-900/30 dark:text-orange-300",
    surfaceClassName:
      "border-orange-100 bg-[linear-gradient(180deg,#FFF8F1_0%,#FFFFFF_100%)] ring-1 ring-orange-100/80 dark:border-orange-900 dark:bg-none dark:bg-orange-950/40 dark:ring-orange-900/70",
  },
  orchestrator_pack: {
    icon: <img src="/img/orchestrator.svg" className="w-5 h-5" alt="" />,
    badgeClassName:
      "border border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-900/30 dark:text-violet-300",
    surfaceClassName:
      "border-violet-100 bg-[linear-gradient(180deg,#F8F5FF_0%,#FFFFFF_100%)] ring-1 ring-violet-100/80 dark:border-violet-900 dark:bg-none dark:bg-violet-950/40 dark:ring-violet-900/70",
  },
};

function isMarketplaceOnboardInstallType(
  installType: string | null | undefined,
): installType is MarketplaceOnboardInstallType {
  return (
    typeof installType === "string" &&
    installType in MARKETPLACE_INSTALL_TYPE_META
  );
}

function getInstallTypeLabel(
  t: (key: string) => string,
  installType: MarketplaceOnboardInstallType,
): string {
  return t(`installTypes.${installType}`);
}

const INSTALL_TYPE_LABELS_EN: Record<MarketplaceOnboardInstallType, string> = {
  single: "Solo agent",
  normal_pack: "Team pack",
  orchestrator_pack: "Orchestrator pack",
};

export function getMarketplaceInstallTypeLabel(
  installType: string | null | undefined,
  t?: (key: string) => string,
): string {
  if (!installType?.trim()) return "—";
  if (installType === "shared_copy") {
    return t ? t("installTypes.shared_copy") : "Shared with Me";
  }
  if (isMarketplaceOnboardInstallType(installType)) {
    return t
      ? getInstallTypeLabel(t, installType)
      : INSTALL_TYPE_LABELS_EN[installType];
  }
  return installType;
}

export const MARKETPLACE_ONBOARD_INSTALL_TYPES = Object.keys(
  MARKETPLACE_INSTALL_TYPE_META,
) as MarketplaceOnboardInstallType[];

export function getMarketplaceInstallTypeMeta(
  installType: string | null | undefined,
): (InstallTypeMeta & { icon: ReactNode }) | null {
  if (!installType) return null;
  if (installType in MARKETPLACE_INSTALL_TYPE_META) {
    return MARKETPLACE_INSTALL_TYPE_META[
      installType as MarketplaceOnboardInstallType
    ];
  }

  return {
    icon: <Package className="h-5 w-5" />,
    badgeClassName:
      "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-900 dark:bg-slate-900/30 dark:text-slate-300",
    surfaceClassName:
      "border-slate-200 bg-[linear-gradient(180deg,#F8FAFC_0%,#FFFFFF_100%)] dark:border-zinc-700 dark:bg-none dark:bg-zinc-900",
  };
}

export function resolveMarketplaceListingInstallType(params: {
  listingIsEmployeePack: boolean;
  versionIsEmployeePack: boolean;
  employeePackMembers: unknown;
}) {
  return resolveMarketplaceOnboardInstallType({
    ...params,
    sourceWorkflowSnapshot: null,
  });
}

function MarketplaceInstallTypeBadge({
  installType,
  className,
}: {
  installType: MarketplaceOnboardInstallType;
  className?: string;
}) {
  const { t } = useTranslation("listings");
  const meta = getMarketplaceInstallTypeMeta(installType);
  if (!meta) return null;

  return (
    <Badge
      variant="outline"
      className={cn(
        "border-0 bg-transparent! rounded-none gap-2 px-0 text-xs text-[#535EA4]! font-semibold max-w-full dark:text-indigo-300!",
        meta.badgeClassName,
        className,
      )}
    >
      <span className="shrink-0">{meta.icon}</span>
      <span className="min-w-0 leading-tight break-words">
        {getInstallTypeLabel(t, installType)}
      </span>
    </Badge>
  );
}

export function MarketplaceInstallTypeDisplay({
  installType,
  className,
}: {
  installType: string | null | undefined;
  className?: string;
}) {
  const { t } = useTranslation("listings");

  if (!installType?.trim()) return null;
  if (isMarketplaceOnboardInstallType(installType)) {
    return (
      <MarketplaceInstallTypeBadge
        installType={installType}
        className={className}
      />
    );
  }

  const meta = getMarketplaceInstallTypeMeta(installType);
  if (!meta) return null;

  return (
    <Badge
      variant="outline"
      className={cn(
        "border-0 bg-transparent! rounded-none gap-2 px-0 text-xs text-[#535EA4]! font-semibold dark:text-indigo-300!",
        meta.badgeClassName,
        className,
      )}
    >
      {meta.icon}
      {getMarketplaceInstallTypeLabel(installType, t)}
    </Badge>
  );
}
